"use client";

import { ChevronsUpDown } from "lucide-react";
import {
	Input as AriaInput,
	type InputProps as AriaInputProps,
	ListBox as AriaListBox,
	type ListBoxProps as AriaListBoxProps,
	type PopoverProps as AriaPopoverProps,
	type ValidationResult as AriaValidationResult,
	ComboBox,
	type ComboBoxProps,
	composeRenderProps,
	Text,
} from "react-aria-components";

import { cn } from "@/utils/cn";

import { Button } from "./button";
import { FieldError, FieldGroup, Label } from "./field";
import { ListBoxCollection, ListBoxHeader, ListBoxItem, ListBoxSection } from "./list-box";
import { Popover } from "./popover";

const ComboboxItem = ListBoxItem;

const _ComboboxHeader = ListBoxHeader;

const _ComboboxSection = ListBoxSection;

const _ComboboxCollection = ListBoxCollection;

const ComboboxInput = ({ className, ...props }: AriaInputProps) => (
	<AriaInput
		className={composeRenderProps(className, className =>
			cn(
				"flex h-10 w-full bg-background px-3 py-2 outline-none file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground",
				/* Disabled */
				"data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
				className,
			),
		)}
		{...props}
	/>
);

const ComboboxPopover = ({ className, ...props }: AriaPopoverProps) => (
	<Popover
		placement="bottom"
		shouldFlip={true}
		shouldUpdatePosition={true}
		className={composeRenderProps(className, className => cn("w-[calc(var(--trigger-width)+4px)]", className))}
		{...props}
	/>
);

const ComboboxListBox = <T extends object>({ className, ...props }: AriaListBoxProps<T>) => (
	<AriaListBox
		className={composeRenderProps(className, className =>
			cn(
				"max-h-[inherit] overflow-auto p-1 outline-none [clip-path:inset(0_0_0_0_round_calc(var(--radius)-2px))]",
				className,
			),
		)}
		{...props}
	/>
);

interface JollyComboBoxProps<T extends object> extends Omit<ComboBoxProps<T>, "children"> {
	label?: string;
	description?: string | null;
	errorMessage?: string | ((validation: AriaValidationResult) => string);
	children: React.ReactNode | ((item: T) => React.ReactNode);
	placeholder?: string;
}

function JollyComboBox<T extends object>({
	label,
	description,
	errorMessage,
	className,
	children,
	placeholder,
	...props
}: JollyComboBoxProps<T>) {
	return (
		<ComboBox
			className={composeRenderProps(className, className => cn("group flex flex-col gap-2", className))}
			{...props}
		>
			<Label>{label}</Label>
			<FieldGroup className="p-0">
				<ComboboxInput placeholder={placeholder} />
				<Button variant="ghost" size="icon" className="mr-1 size-6 p-1" aria-label="Show options">
					<ChevronsUpDown aria-hidden="true" className="size-4 opacity-50" />
				</Button>
			</FieldGroup>
			{description && (
				<Text className="text-sm text-muted-foreground" slot="description">
					{description}
				</Text>
			)}
			<FieldError>{errorMessage}</FieldError>
			<ComboboxPopover>
				<ComboboxListBox>{children}</ComboboxListBox>
			</ComboboxPopover>
		</ComboBox>
	);
}

export { ComboboxItem, JollyComboBox };
